import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import VendorLayout from '@/components/layouts/VendorLayout';
import {
  FileText, Send, Clock, CheckCircle, Loader2,
  TrendingUp, XCircle, Eye, Award, ArrowUpRight
} from 'lucide-react';
import { getApiUrl } from '@/lib/api';

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  loading: boolean;
}

const StatCard = ({ title, value, icon: Icon, color, loading }: StatCardProps) => (
  <div className="bg-[#18181b] rounded-xl border border-white/5 p-5">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-zinc-500 mb-1">{title}</p>
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin text-zinc-600" />
        ) : (
          <p className="text-2xl font-bold text-white">{value}</p>
        )}
      </div>
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

interface ReputationCardProps {
  reputation: unknown;
  loading: boolean;
}

const ReputationCard = ({ reputation, loading }: ReputationCardProps) => {
  if (loading) return (
    <div className="bg-[#18181b] rounded-xl border border-white/5 p-6">
      <div className="flex items-center justify-center py-4"><Loader2 className="w-6 h-6 animate-spin text-zinc-600" /></div>
    </div>
  );

  if (!reputation) return null;

  const rep = reputation as { reputation_score: number; acceptance_rate: number; total_delivered: number; total_accepted: number };
  const score = rep.reputation_score;
  const badgeColor = score >= 1.2 ? 'text-emerald-400 bg-emerald-500/10' : score >= 0.8 ? 'text-blue-400 bg-blue-500/10' : 'text-rose-400 bg-rose-500/10';
  const badgeLabel = score >= 1.2 ? 'Excellent' : score >= 0.8 ? 'Good' : 'Needs Improvement';

  return (
    <div className="bg-[#18181b] rounded-xl border border-white/5 p-6" data-testid="vendor-reputation-card">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-amber-500/10">
            <Award className="w-5 h-5 text-amber-400" />
          </div>
          <h2 className="text-base font-semibold text-white">Vendor Reputation</h2>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${badgeColor}`} data-testid="reputation-badge">
          {badgeLabel}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="text-center">
          <p className="text-2xl font-bold text-white" data-testid="reputation-score">{score.toFixed(1)}</p>
          <p className="text-xs text-zinc-500 mt-1">Rep. Score</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-white" data-testid="acceptance-rate">
            {(rep.acceptance_rate * 100).toFixed(0)}%
          </p>
          <p className="text-xs text-zinc-500 mt-1">Accept Rate</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-white" data-testid="total-delivered">{rep.total_delivered}</p>
          <p className="text-xs text-zinc-500 mt-1">Delivered</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-white" data-testid="total-accepted">{rep.total_accepted}</p>
          <p className="text-xs text-zinc-500 mt-1">Accepted</p>
        </div>
      </div>
    </div>
  );
};

export default function VendorDashboard() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState<unknown>(null);
  const [reputation, setReputation] = useState<unknown>(null);
  const [recentPitches, setRecentPitches] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(true);
  const [repLoading, setRepLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, pitchesRes, repRes] = await Promise.all([
        fetch(getApiUrl('/api/v2/vendor/engagement/dashboard'), { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(getApiUrl('/api/v2/vendor/pitches-with-metrics?limit=5'), { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(getApiUrl('/api/v2/vendor/reputation'), { headers: { 'Authorization': `Bearer ${token}` } }),
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (pitchesRes.ok) setRecentPitches(await pitchesRes.json());
      if (repRes.ok) setReputation(await repRes.json());
    } catch (err) {
      console.error('Failed to fetch:', err);
    } finally {
      setLoading(false);
      setRepLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const STATUS_BADGE: Record<string, string> = {
    DRAFT: 'bg-zinc-500/10 text-zinc-400',
    SUBMITTED: 'bg-rose-500/10 text-rose-400',
    AI_EVALUATED: 'bg-blue-500/10 text-blue-400',
    VALUABLE: 'bg-emerald-500/10 text-emerald-400',
    DECLINED: 'bg-rose-500/10 text-rose-400',
    DELIVERED: 'bg-blue-500/10 text-blue-400',
    ACCEPTED: 'bg-emerald-500/10 text-emerald-400',
    REJECTED: 'bg-rose-500/10 text-rose-400',
  };

  const recentList = recentPitches as Array<{ id: string; product_name?: string; category?: string; status_v2?: string; view_count?: number; outcome?: string; created_at?: string }>;
  const statsObj = stats as { total_pitches?: number; pending_review?: number; delivered?: number; accepted?: number } | null;

  return (
    <VendorLayout>
      <div className="space-y-6" data-testid="vendor-dashboard">
        {/* Welcome */}
        <div className="bg-gradient-to-r from-rose-500/10 to-rose-600/5 rounded-2xl p-6 border border-rose-500/20">
          <h1 className="text-2xl font-bold text-white mb-2">
            Welcome back, {user?.name?.split(' ')[0] || 'Vendor'}!
          </h1>
          <p className="text-zinc-400">Track your pitches and see how merchants engage with your products.</p>
        </div>

        {/* Reputation Card */}
        <ReputationCard reputation={reputation} loading={repLoading} />

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard title="Total Pitches" value={statsObj?.total_pitches ?? 0} icon={FileText} color="bg-white/5 text-zinc-400" loading={loading} />
          <StatCard title="Pending Review" value={statsObj?.pending_review ?? 0} icon={Clock} color="bg-rose-500/10 text-rose-400" loading={loading} />
          <StatCard title="Delivered" value={statsObj?.delivered ?? 0} icon={Eye} color="bg-blue-500/10 text-blue-400" loading={loading} />
          <StatCard title="Accepted" value={statsObj?.accepted ?? 0} icon={CheckCircle} color="bg-emerald-500/10 text-emerald-400" loading={loading} />
        </div>

        {/* Quick Actions */}
        <div className="bg-[#18181b] rounded-xl border border-white/5 p-6">
          <h2 className="text-base font-semibold text-white mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link to="/vendor/pitches/new" data-testid="quick-action-new-pitch"
              className="flex items-center gap-4 p-4 rounded-lg border border-white/5 hover:border-rose-500/30 hover:bg-rose-500/5 transition-colors group">
              <div className="p-3 rounded-lg bg-rose-500/10 text-rose-400 group-hover:bg-rose-500/20">
                <Send className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium text-white">Submit New Pitch</p>
                <p className="text-sm text-zinc-500">Create and send a new pitch</p>
              </div>
            </Link>
            <Link to="/vendor/pitches" data-testid="quick-action-view-pitches"
              className="flex items-center gap-4 p-4 rounded-lg border border-white/5 hover:border-white/10 hover:bg-white/5 transition-colors group">
              <div className="p-3 rounded-lg bg-white/5 text-zinc-400 group-hover:bg-white/10">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium text-white">View All Pitches</p>
                <p className="text-sm text-zinc-500">Track status and engagement</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Recent Activity with Metrics */}
        <div className="bg-[#18181b] rounded-xl border border-white/5 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-white">Recent Pitches</h2>
            {recentList.length > 0 && (
              <Link to="/vendor/pitches" className="text-sm text-rose-400 hover:text-rose-300 font-medium flex items-center gap-1">
                View all <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
          {loading ? (
            <div className="text-center py-8"><Loader2 className="w-8 h-8 animate-spin text-zinc-600 mx-auto" /></div>
          ) : recentList.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
              <p>No pitches yet</p>
              <p className="text-sm">Submit your first pitch to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-zinc-500 text-xs uppercase tracking-wider border-b border-white/5">
                    <th className="text-left py-2 pr-4 font-medium">Pitch</th>
                    <th className="text-left py-2 px-3 font-medium">Status</th>
                    <th className="text-center py-2 px-3 font-medium">Views</th>
                    <th className="text-left py-2 px-3 font-medium">Outcome</th>
                    <th className="text-right py-2 pl-3 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {recentList.map((p) => (
                    <tr key={p.id} className="hover:bg-white/5 transition-colors">
                      <td className="py-3 pr-4">
                        <Link to={`/vendor/pitches/${p.id}`} className="font-medium text-white hover:text-rose-300 truncate block max-w-[200px]">
                          {p.product_name}
                        </Link>
                        {p.category && <p className="text-xs text-zinc-600">{p.category}</p>}
                      </td>
                      <td className="py-3 px-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_BADGE[p.status_v2 ?? ''] || 'bg-white/5 text-zinc-400'}`} data-testid={`pitch-status-${p.id}`}>
                          {p.status_v2}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className="text-zinc-300 font-medium" data-testid={`pitch-views-${p.id}`}>{p.view_count}</span>
                      </td>
                      <td className="py-3 px-3">
                        {p.outcome ? (
                          <span className={`inline-flex items-center gap-1 text-xs font-medium ${
                            p.outcome === 'Accepted' ? 'text-emerald-400' :
                            p.outcome === 'Rejected' ? 'text-rose-400' :
                            'text-zinc-500'
                          }`} data-testid={`pitch-outcome-${p.id}`}>
                            {p.outcome === 'Accepted' && <CheckCircle className="w-3 h-3" />}
                            {p.outcome === 'Rejected' && <XCircle className="w-3 h-3" />}
                            {p.outcome === 'Pending' && <Clock className="w-3 h-3" />}
                            {p.outcome}
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-600">—</span>
                        )}
                      </td>
                      <td className="py-3 pl-3 text-right text-xs text-zinc-500">{formatDate(p.created_at ?? '')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </VendorLayout>
  );
}
